// hooks/useSubscription.js
// Hook to check user's subscription and trial status
// FIXED: Pro users bypass trial check entirely
// Last updated: November 28, 2025

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://www.whydiditdie.com';

export const useSubscription = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [planType, setPlanType] = useState('free_trial');
  const [trialInfo, setTrialInfo] = useState({
    isTrialExpired: false,
    daysRemaining: null,
    daysOverdue: 0,
    trialEndDate: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!user?.email) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      logger.info('🔍 Checking subscription status for:', user.email);

      // Check backend subscription status first
      const response = await fetch(
        `${BACKEND_URL}/api/paystack/subscription/status?email=${encodeURIComponent(user.email)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      const data = await response.json();
      logger.info('📋 Subscription status:', data);

      // If backend says they're Pro, they're Pro - NO trial check needed
      if (data.status && data.plan_type === 'pro') {
        logger.info('✅ User is Pro - skipping trial check');
        setPlanType('pro');
        setSubscription(data.subscription);
        setTrialInfo({
          isTrialExpired: false,
          daysRemaining: null,
          daysOverdue: 0,
          trialEndDate: null
        });
        setIsLoading(false);
        return;
      }

      // If they have an active subscription, they're Pro
      if (data.status && data.subscription) {
        logger.info('✅ User has active subscription');
        setSubscription(data.subscription);
        setPlanType('pro');
        setTrialInfo({
          isTrialExpired: false,
          daysRemaining: null,
          daysOverdue: 0,
          trialEndDate: null
        });
        setIsLoading(false);
        return;
      }

      // Only check trial status if NOT pro
      logger.info('🔄 User is not Pro, checking trial status...');
      setPlanType(data.plan_type || 'free_trial');

      // Get trial info from profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('plan_type, trial_start_date, trial_end_date, trial_days')
        .eq('email', user.email)
        .single();

      if (profileError) {
        logger.error('Profile fetch error:', profileError);
        setIsLoading(false);
        return;
      }

      // Double-check: if profile says Pro, they're Pro
      if (profile?.plan_type === 'pro') {
        logger.info('✅ Profile confirms Pro status');
        setPlanType('pro');
        setTrialInfo({
          isTrialExpired: false,
          daysRemaining: null,
          daysOverdue: 0,
          trialEndDate: null
        });
        setIsLoading(false);
        return;
      }

      // Calculate trial expiration for free_trial users only
      if (profile?.plan_type === 'free_trial') {
        const now = new Date();
        let trialEnd = null;

        if (profile.trial_end_date) {
          trialEnd = new Date(profile.trial_end_date);
        } else if (profile.trial_start_date) {
          trialEnd = new Date(profile.trial_start_date);
          trialEnd.setDate(trialEnd.getDate() + (profile.trial_days || 14));
        }

        if (trialEnd) {
          const diffTime = trialEnd - now;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          logger.info('📅 Trial end date:', trialEnd, 'Days remaining:', diffDays);

          setTrialInfo({
            isTrialExpired: diffDays < 0,
            daysRemaining: diffDays > 0 ? diffDays : 0,
            daysOverdue: diffDays < 0 ? Math.abs(diffDays) : 0,
            trialEndDate: trialEnd
          });
        } else {
          // No trial end date set - assume valid trial
          logger.info('⚠️ No trial end date set, assuming valid trial');
          setTrialInfo({
            isTrialExpired: false,
            daysRemaining: 2,
            daysOverdue: 0,
            trialEndDate: null
          });
        }
      }

    } catch (err) {
      logger.error('❌ Failed to fetch subscription:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user?.email, token]);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      fetchSubscriptionStatus();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.email, fetchSubscriptionStatus]);

  // Computed properties
  const isPro = planType === 'pro';
  const isFreeTrial = planType === 'free_trial';
  const isActive = subscription?.status === 'active';
  const canAccessFeatures = isPro || (isFreeTrial && !trialInfo.isTrialExpired);
  
  // Calculate subscription days remaining
  const getDaysRemaining = () => {
    if (!subscription?.ends_at) return null;
    const endsAt = new Date(subscription.ends_at);
    const now = new Date();
    const diffTime = endsAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return {
    // State
    subscription,
    planType,
    isLoading,
    error,
    
    // Trial info
    trialInfo,
    isTrialExpired: trialInfo.isTrialExpired,
    trialDaysRemaining: trialInfo.daysRemaining,
    trialDaysOverdue: trialInfo.daysOverdue,
    
    // Computed
    isPro,
    isFreeTrial,
    isActive,
    canAccessFeatures,
    daysRemaining: getDaysRemaining(),
    
    // Actions
    refresh: fetchSubscriptionStatus
  };
};

export default useSubscription;